/**
 * "Set someone up to sign at a machine" — issuing a badge number and a PIN.
 *
 * WHAT THIS WORKFLOW IS FOR. Field hands have no logins. They sign for work by scanning the QR
 * sticker on a machine and typing two things: a badge number, which is printed on their hard hat and
 * is public, and a PIN, which is not. Until somebody in the office issues that pair, the person
 * physically cannot sign for anything. This is the tour for the person who issues it.
 *
 * WHO IT IS FOR, AND WHY IT IS NOT THE FOREMAN. Every step is gated on `hr:manage`, and the tour on
 * the `hr` module, because BOTH are needed to walk it end to end:
 *
 *   - the badge goes through PUT /employees/{id}, which requires `hr:manage`
 *     (src/modules/operations/hr/backend/app/api/employees.py:240), and the "Give a badge number"
 *     button is rendered only for `canManageEmployee` (FieldAccessPanel.jsx:335);
 *   - `hr` is what puts HR in the nav at all (getAccessibleNavModules → shared/constants/modules.js).
 *
 * Among the seeded roles that pair belongs to `admin` and `accounting` only
 * (identity backend database/session.py). A FOREMAN holds `hr:manage_field_pin` and can reset a PIN
 * for someone who already has a badge — but they cannot create the badge, `Set PIN` is disabled
 * until one exists (FieldAccessPanel.jsx:420), and they have no HR entry in their nav to reach any
 * of it. So this tour drops out of their menu entirely rather than walking them into a wall. The
 * foreman's "reset a PIN" tour is a SEPARATE data file, and it needs the missing HR navigation
 * decided first — it is not a variant of this one.
 *
 * WHY EVERY STEP CARRIES route '/hr'. HR keeps its position in useState, not in the URL: one route
 * renders the dashboard, the roster, one employee, and that employee's Field Access tab (hr/App.jsx
 * :31, EmployeeDetails.jsx:14). The engine navigates only when the path differs (TourProvider.jsx
 * :138), so repeating '/hr' gets a reader who is elsewhere INTO HR and then never fires again —
 * which matters, because a second navigate to '/hr' would reset the tabs and throw the reader back
 * to the dashboard mid-tour. `screen` is what separates the four states for the anchor guard.
 *
 * ══ WHAT WAS WRONG WITH THIS TOUR, AND WHAT EACH GATE IS NOW HOLDING ═════════════════════════════
 *
 * The owner walked this tour on staging and finished it with no badge and no PIN, spotlighted on a
 * greyed-out button. Every one of the four defects behind that is a gate that was not there:
 *
 *  1. `type-the-badge` was advanceOn NEXT, so NO step in the whole tour ever required a keystroke.
 *     It is now `filled` on the badge box — the tour will not move until there is something in it.
 *
 *  2. `save-the-badge` advanced on the CLICK, and the panel refuses a blank badge locally without
 *     ever issuing the PUT (FieldAccessPanel.jsx:189-192), so the tour walked on from a save that
 *     never happened. It now waits for the REQUEST, which is the first moment anything is true.
 *
 *  3. `set-a-pin` then spotlighted `Set PIN`, which carries `disabled={!hasBadge}`
 *     (FieldAccessPanel.jsx:424) — a dead control, because no badge had been saved. Fixing 1 and 2
 *     is what makes that button live by the time the step opens.
 *
 *  4. `save-the-pin` advanced on the click too, so a PIN the SERVER REFUSED ("that PIN reads as a
 *     date") still carried the reader on to "Tell them now — this is the only time it is shown",
 *     and the tour ended as though a PIN had been issued. It now waits for the reveal panel, which
 *     `issuedPin` gates and which is set at FieldAccessPanel.jsx:245 only after the PUT succeeds.
 *     A refused PIN now holds the step instead of lying about it.
 *
 * The shape of 2 and 4 is the same shape as the owner's sticker complaint: a walkthrough asserting a
 * state of the world that is not true. Neither `clicked` gate could tell the difference, because a
 * click is not evidence that anything happened — which is why the two steps that WRITE are the two
 * that wait for something only a real write produces.
 *
 * ── THE PRACTICE RUN NEEDS THE BADGE TO EXIST WITHOUT BEING SAVED ───────────────────────────────
 * Practice mode blocks the PUT, and the panel then re-READS the employee (loadStatus) — and a read
 * goes straight through to the server, which has never heard of this badge. So `hasBadge` stayed
 * false, `Set PIN` stayed greyed out, and the entire second half of this tour was unreachable on a
 * practice run: the same dead end as the bug, arrived at from the other direction. FieldAccessPanel
 * now keeps the badge the server ACCEPTED (see `savedBadge` there) rather than only what a later
 * read hands back, which makes the PIN half reachable.
 */
import { defineTour, AUDIENCE, ADVANCE_ON } from '../tourSchema'

// Every step needs the same two things, and repeating them 13 times invites one to drift.
const AT = (screen) => ({ route: '/hr', screen, module: 'hr', permission: 'hr:manage' })

export default defineTour({
  id: 'issue-field-pin',
  title: 'Set someone up to sign at a machine',
  summary: 'Give a field hand a badge number and a PIN.',
  audience: AUDIENCE.ONBOARDING,
  module: 'hr',

  steps: [
    {
      ...AT('/hr'),
      id: 'open-the-roster',
      anchor: 'hr.nav.employees',
      title: 'Start with the employee list',
      body: 'Crews out on site don’t have logins. To let someone sign for work on a machine, you give them two things from their record: a badge number and a PIN. Open Employees to start.',
      // The three "get to the next screen" steps of this tour wait for the SCREEN, not for the click
      // that was supposed to produce it. A click that did not land — a mis-hit, a tab that refused,
      // a page still loading — used to carry the tour forward anyway, and every step after it then
      // pointed at controls that were not there.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'hr.roster.search' },
    },
    {
      ...AT('/hr#roster'),
      id: 'find-the-person',
      anchor: 'hr.roster.search',
      title: 'Find the person',
      body: 'Type any part of their name, their employee number, their email, or their department. The list narrows as you type — you don’t need to press anything.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      ...AT('/hr#roster'),
      id: 'open-their-record',
      anchor: 'hr.roster.open-employee',
      title: 'Open their record',
      body: 'The eye opens one person. We’re pointing at the top row as an example — click the eye on whichever row is the person you’re setting up.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'hr.employee.field-access' },
      stuckHint: 'Nobody in the list? Clear the search box above — the tour is waiting for a record to open.',
    },
    {
      ...AT('/hr#employee-details'),
      id: 'open-field-access',
      anchor: 'hr.employee.field-access',
      title: 'Go to Field Access',
      body: 'Everything about signing at a machine lives on this tab: their badge number, their PIN, and whether anyone has been getting that PIN wrong.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'field-pin.badge.edit' },
    },
    {
      ...AT('/hr#field-access'),
      id: 'give-a-badge',
      anchor: 'field-pin.badge.edit',
      title: 'The badge number comes first',
      body: 'The badge number is the name the machine knows them by, and it goes on their hard hat. Do this one first — a PIN on its own is no use, because there’s nothing for it to be typed against.',
      // Waits for the BOX, not for the click. Same reason everywhere below: a click is not evidence
      // that anything opened, and this one is the step that puts the reader in front of the form the
      // next two steps live in.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'field-pin.badge.input' },
      stuckHint: 'No “Give a badge number” button? Then this account can see field access but not change it — that needs HR.',
    },
    {
      ...AT('/hr#field-access-badge'),
      id: 'type-the-badge',
      anchor: 'field-pin.badge.input',
      title: 'Keep it short',
      body: 'They’ll be reading this off a hard hat and typing it with gloves on, so short is kind. It has to be unique in your company — two people can’t share one. Type one in to carry on.',
      // THE ONE THAT BROKE THE TOUR. This was NEXT, which meant nobody was ever asked to type
      // anything, and the reader arrived at Save with an empty box and a panel that refused it.
      advanceOn: ADVANCE_ON.DO,
      until: { filled: 'field-pin.badge.input' },
    },
    {
      ...AT('/hr#field-access-badge'),
      id: 'save-the-badge',
      anchor: 'field-pin.badge.save',
      title: 'Save it',
      body: 'That stores the badge number on their record. Nothing works yet — they still can’t sign at anything until they have a PIN as well.',
      // Waits for the REQUEST. Clicking Save with an empty box shows "Enter a badge number, or
      // cancel." and issues nothing (FieldAccessPanel.jsx:189-192) — on a click gate the tour walked
      // on from that, which is how the owner reached the PIN steps with no badge on file.
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'PUT /employees/' },
      stuckHint: 'If the badge box is empty, or the panel is showing a reason in red, the save hasn’t gone through yet.',
    },
    {
      ...AT('/hr#field-access-with-badge'),
      id: 'set-a-pin',
      anchor: 'field-pin.set',
      title: 'Now the PIN',
      body: 'The badge number is on show for anyone to read. The PIN is the half only they know, so it’s the part that actually proves it’s them standing at the machine.',
      // The rules panel only exists while the PIN form is open, so this waits for the form itself
      // rather than for a click on the button that opens it.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'field-pin.rules' },
      stuckHint: 'Set PIN stays greyed out until a badge number is saved — go back a step if that one didn’t take.',
    },
    {
      ...AT('/hr#field-access-pin'),
      id: 'pin-rules',
      anchor: 'field-pin.rules',
      title: 'Read these before you pick one',
      body: 'Easy PINs get turned down — counting up, repeated pairs, and anything that reads like a birthday. Worth knowing now rather than after you’ve chosen one.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      ...AT('/hr#field-access-pin'),
      id: 'generate-a-pin',
      anchor: 'field-pin.generate',
      title: 'Let the app pick one',
      body: 'This fills in a random PIN that has already been checked against those rules. It’s the safer choice: people asked to invent one tend to reach for the same handful of numbers. Press it.',
      // A click IS the step here — the step is "press this button" and nothing downstream depends on
      // which PIN ends up in the box, so there is nothing stronger worth waiting for.
      advanceOn: ADVANCE_ON.CLICK,
    },
    {
      ...AT('/hr#field-access-pin'),
      id: 'save-the-pin',
      anchor: 'field-pin.save',
      title: 'Save the PIN',
      body: 'This is the moment they can start signing. If they already had a PIN, this one replaces it and the old one stops working immediately.',
      // Waits for the PIN ITSELF TO APPEAR, which is the only proof the server accepted it. The
      // server refuses easy PINs (runs, repeats, anything that reads like a date) and on a click gate
      // a refusal still carried the reader on to "Tell them now", ending the tour as though a PIN had
      // been issued when none had. `field-pin.reveal` renders off `issuedPin`, set only after the PUT
      // succeeds (FieldAccessPanel.jsx:245), so a refused PIN now holds this step.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'field-pin.reveal' },
      stuckHint: 'If the panel is showing a reason in red, that PIN was turned down — generate another and set it.',
    },
    {
      ...AT('/hr#field-access-issued'),
      id: 'read-it-out',
      anchor: 'field-pin.reveal',
      title: 'Tell them now',
      body: 'This is the only time the PIN is ever shown. Nobody can look it up afterwards — not you, not whoever set the system up. Read it to them while they’re in front of you, or copy it and send it the way you’d normally send something private.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      ...AT('/hr#field-access-issued'),
      id: 'finish-up',
      anchor: 'field-pin.done',
      title: 'Close it out',
      body: 'That clears the PIN off the screen. If it turns out they never got it, don’t go hunting — just set a new one. It takes seconds and you don’t need the old one.',
      advanceOn: ADVANCE_ON.CLICK,
    },
  ],
})
